import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { useOpsActivateService, useOpsDeactivateService } from '@/api/operations-org-hooks';
import type { OpsServiceDetail, OpsServiceStatus } from '@/api/operations';

interface Props {
  service: OpsServiceDetail;
  /** Called after a successful toggle so the parent can show a toast / refetch. */
  onSuccess: (action: 'activate' | 'deactivate') => void;
}

/**
 * Activate / Deactivate toggle UI for the service detail header action zone.
 *
 * Two-step confirm dialog (plain Dialog, not ConfirmDialog — ConfirmDialog
 * lacks a confirmDisabled prop; we need to gate on a non-empty reason field):
 *   1. Shows service name + org ID + action-specific warning.
 *   2. Requires a non-empty reason string before confirm is enabled.
 *
 * 501 from the backend means the category doesn't support lifecycle yet —
 * surfaced as a warning inside the dialog so the operator understands without
 * dismissing the form.
 *
 * Only renders for status 'active' (→ Deactivate) or 'terminated' (→ Activate).
 * Other statuses (pending, error, suspended) need investigation, not a toggle.
 */
export function OpsServiceToggleDialog({ service, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [unsupportedError, setUnsupportedError] = useState(false);

  const activate = useOpsActivateService();
  const deactivate = useOpsDeactivateService();

  const status: OpsServiceStatus = service.status as OpsServiceStatus;
  const isActive = status === 'active';
  const isTerminated = status === 'terminated';

  // Only show the toggle for toggleable states.
  if (!isActive && !isTerminated) return null;

  const action = isActive ? 'deactivate' : 'activate';
  const mutation = isActive ? deactivate : activate;
  const isDeactivate = action === 'deactivate';

  const displayName = service.service_name || `Service #${service.id}`;

  const handleOpen = () => {
    setReason('');
    setUnsupportedError(false);
    mutation.reset();
    setOpen(true);
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    setOpen(false);
    setReason('');
    setUnsupportedError(false);
    mutation.reset();
  };

  const handleConfirm = async () => {
    if (!reason.trim() || mutation.isPending) return;
    setUnsupportedError(false);
    try {
      await mutation.mutateAsync({ serviceId: service.id, reason: reason.trim() });
      setOpen(false);
      setReason('');
      mutation.reset();
      onSuccess(action);
    } catch (err: unknown) {
      // 501 = category doesn't support lifecycle yet — surface inline.
      const httpStatus = (err as { response?: { status?: number } })?.response?.status;
      if (httpStatus === 501) {
        setUnsupportedError(true);
      }
      // Other errors stay visible via mutation.isError; dialog stays open.
    }
  };

  return (
    <>
      <Button
        variant={isDeactivate ? 'danger' : 'primary'}
        size="sm"
        onClick={handleOpen}
      >
        {isDeactivate ? 'Deactivate' : 'Activate'}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {isDeactivate ? 'Deactivate service' : 'Activate service'}
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              {/* Service summary */}
              <Card flat>
                <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)', fontSize: 'var(--vn-font-size-sm)' }}>
                  <div>
                    <span style={{ color: 'var(--vn-fg-muted)' }}>Service: </span>
                    <strong>{displayName}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--vn-fg-muted)' }}>Org ID: </span>
                    <span style={{ fontFamily: 'monospace' }}>#{service.organization_id}</span>
                  </div>
                </CardBody>
              </Card>

              {/* Action-specific warning */}
              <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}>
                {isDeactivate ? (
                  <>
                    This will <strong>soft-terminate</strong> the service and park all
                    service zones. The service can be restored later via Activate.
                  </>
                ) : (
                  <>
                    This will <strong>restore</strong> the terminated service and
                    re-enable its service zones.
                  </>
                )}
              </p>

              {/* 501 unsupported-category warning */}
              {unsupportedError && (
                <Alert tone="warning">
                  This service category does not yet support lifecycle management.
                  The operation was not applied.
                </Alert>
              )}

              {/* Generic error (non-501) */}
              {mutation.isError && !unsupportedError && (
                <Alert tone="danger">
                  Operation failed. Please try again or contact an admin.
                </Alert>
              )}

              {/* Reason field — required, gates confirm */}
              <Field label="Reason (required)">
                {({ id }) => (
                  <Input
                    id={id}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={
                      isDeactivate
                        ? 'e.g. Customer requested termination'
                        : 'e.g. Customer requested reactivation'
                    }
                    disabled={mutation.isPending}
                  />
                )}
              </Field>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" disabled={mutation.isPending} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant={isDeactivate ? 'danger' : 'primary'}
              disabled={!reason.trim() || mutation.isPending}
              onClick={handleConfirm}
            >
              {mutation.isPending
                ? isDeactivate ? 'Deactivating…' : 'Activating…'
                : isDeactivate ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
